/**
 * DiscussionOverlay Component - Main Floating Discussion Container
 *
 * The main component that houses the entire discussion UI:
 * - ChatHeader with trip name and minimize button
 * - MessageList with scrollable message history
 * - InputArea for composing and sending messages
 *
 * Features:
 * - Floating panel (desktop): Bottom-right, 400x600px, expandable
 * - Full-screen (mobile): Covers entire viewport
 * - Smooth slide-in/slide-out animations
 * - Real-time message integration via useDiscussionMessages hook
 * - Message posting via usePostMessage hook
 *
 * Positioning Strategy:
 * - Desktop (≥768px): Fixed position, bottom-right corner, z-index 1300
 * - Mobile (<768px): Full-screen overlay, z-index 1300
 * - Uses Material-UI Paper for elevation and shadows
 */

import React from 'react';
import { Box, Paper, Alert, Snackbar } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTrip, useAuth } from '@trip-planner-monorepo/shared';
import { useDiscussionMessages, usePostMessage } from '../../hooks';
import ChatHeader from './ChatHeader';
import MessageList from '../MessageList/MessageList';
import InputArea from '../InputArea/InputArea';

/**
 * DiscussionOverlay Component
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether overlay is visible
 * @param {Function} props.onClose - Callback to close overlay
 * @param {string} [props.tripId] - Trip ID (optional, uses TripContext if not provided)
 */
const DiscussionOverlay = ({ isOpen, onClose, tripId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Contexts
  const { currentTrip, selectedTripId, effectiveUserId } = useTrip();
  const { currentUser } = useAuth();

  // Use provided tripId or fall back to TripContext
  const activeTripId = tripId || selectedTripId;

  // Hooks for real-time messages
  const { messages, loading, error: messageError } = useDiscussionMessages(activeTripId, {
    limitCount: 50,
    orderDirection: 'asc', // Oldest first (chat style)
    autoLoad: true
  });

  const { postMessage, posting, error: postError, clearError } = usePostMessage();

  /**
   * Handle sending a message
   * @param {string} text - Message text content
   * @param {Object} [pageContext] - Current page context (optional, for AI context awareness)
   */
  const handleSendMessage = async (text, pageContext = null) => {
    try {
      await postMessage(text, { additionalMetadata: { pageContext } });
    } catch (error) {
      // Error already set in hook state, will be displayed in Snackbar
      console.error('[DiscussionOverlay] Failed to send message:', error);
    }
  };

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  // Determine trip name for header
  const tripName = currentTrip?.name;

  // Determine trip owner info for members dialog
  // For owned trips: currentUser is the owner
  // For shared trips: use sharedByEmail from trip pointer
  const tripOwnerEmail = currentTrip?.isShared
    ? (currentTrip.sharedByEmail || `owner-${effectiveUserId?.substring(0, 6)}@...`)
    : currentUser?.email;

  return (
    <>
      {/* Overlay Container */}
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          // Desktop: Floating panel bottom-right
          ...(isMobile
            ? {
                // Mobile: Full-screen
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                borderRadius: 0
              }
            : {
                // Desktop: Floating panel
                bottom: 20,
                right: 20,
                width: 400,
                height: 600,
                borderRadius: '12px',
                // Glassmorphism effect (optional)
                // backgroundColor: 'rgba(255, 255, 255, 0.95)',
                // backdropFilter: 'blur(10px)'
              }),
          // Slide-in animation
          animation: 'slideIn 0.3s ease-out',
          '@keyframes slideIn': {
            from: {
              opacity: 0,
              transform: isMobile ? 'translateY(100%)' : 'translateY(20px)'
            },
            to: {
              opacity: 1,
              transform: 'translateY(0)'
            }
          }
        }}
      >
        {/* Header */}
        <ChatHeader
          tripName={tripName}
          trip={currentTrip}
          ownerEmail={tripOwnerEmail}
          currentUserEmail={currentUser?.email}
          isSharedTrip={currentTrip?.isShared}
          onMinimize={onClose}
        />

        {/* Message List */}
        <MessageList
          messages={messages}
          loading={loading}
          currentUserId={currentUser?.uid}
          tripId={activeTripId}
          ownerId={effectiveUserId}
        />

        {/* Input Area */}
        <InputArea
          onSendMessage={handleSendMessage}
          disabled={posting || !currentUser || !activeTripId}
          tripId={activeTripId}
          ownerId={effectiveUserId}
          placeholder={
            !currentUser
              ? 'Sign in to chat'
              : !activeTripId
              ? 'Select a trip to chat'
              : 'Type a message...'
          }
        />
      </Paper>

      {/* Error Snackbar for Message Loading Errors */}
      {messageError && (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          onClose={() => {
            /* Error state managed by hook */
          }}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="error" variant="filled">
            {messageError.message || 'Failed to load messages'}
          </Alert>
        </Snackbar>
      )}

      {/* Error Snackbar for Post Errors */}
      {postError && (
        <Snackbar
          open={true}
          autoHideDuration={6000}
          onClose={clearError}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert severity="error" variant="filled" onClose={clearError}>
            {postError.message || 'Failed to send message'}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default DiscussionOverlay;
